import { useState } from 'react';
import { 
  AlertCircle, 
  ExternalLink, 
  ChevronDown, 
  ChevronUp,
  Linkedin,
  Facebook,
  Instagram,
  Key,
  CheckCircle2,
  Copy,
  Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function SocialMediaSetupGuide({ onClose }) {
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [copiedText, setCopiedText] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const platforms = [
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      steps: [
        {
          title: 'Create a LinkedIn Developer App',
          content: (
            <div className="space-y-3">
              <p>1. Go to <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">LinkedIn Developer Portal</a></p>
              <p>2. Click "Create App"</p>
              <p>3. Fill in your app details:</p>
              <ul className="list-disc list-inside ml-4 text-slate-400">
                <li>App name: "Cornerstone Social Manager"</li>
                <li>LinkedIn Page: Select your company page</li>
                <li>App logo: Upload your logo</li>
              </ul>
              <p>4. Accept the terms and create the app</p>
            </div>
          )
        },
        {
          title: 'Configure Products & Permissions',
          content: (
            <div className="space-y-3">
              <p>1. Go to the "Products" tab in your app</p>
              <p>2. Request access to:</p>
              <ul className="list-disc list-inside ml-4 text-slate-400">
                <li><strong>Share on LinkedIn</strong> - For posting content</li>
                <li><strong>Sign In with LinkedIn using OpenID Connect</strong></li>
              </ul>
              <p>3. Go to "Auth" tab and add OAuth 2.0 redirect URLs:</p>
              <div className="bg-slate-900 p-3 rounded font-mono text-sm flex items-center justify-between">
                <span>https://yourdomain.com/api/auth/linkedin/callback</span>
                <button onClick={() => copyToClipboard('https://yourdomain.com/api/auth/linkedin/callback', 'linkedin-redirect')} className="text-slate-400 hover:text-white">
                  {copiedText === 'linkedin-redirect' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )
        },
        {
          title: 'Get Your Credentials',
          content: (
            <div className="space-y-3">
              <p>From the "Auth" tab, copy:</p>
              <ul className="list-disc list-inside ml-4 text-slate-400">
                <li><strong>Client ID</strong></li>
                <li><strong>Client Secret</strong> (click to reveal)</li>
              </ul>
              <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded mt-3">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Note: LinkedIn API access for posting requires a verified Company Page
                </p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'facebook',
      name: 'Facebook / Meta',
      icon: Facebook,
      color: 'text-blue-600',
      bgColor: 'bg-blue-600/10',
      steps: [
        {
          title: 'Create a Meta Developer App',
          content: (
            <div className="space-y-3">
              <p>1. Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Meta for Developers</a></p>
              <p>2. Click "Create App"</p>
              <p>3. Select "Business" as the app type</p>
              <p>4. Fill in app details and create</p>
            </div>
          )
        },
        {
          title: 'Add Facebook Login & Pages API',
          content: (
            <div className="space-y-3">
              <p>1. In your app dashboard, click "Add Product"</p>
              <p>2. Add <strong>Facebook Login</strong></p>
              <p>3. Add <strong>Pages API</strong> (for posting to Pages)</p>
              <p>4. Configure OAuth redirect URI:</p>
              <div className="bg-slate-900 p-3 rounded font-mono text-sm flex items-center justify-between">
                <span>https://yourdomain.com/api/auth/facebook/callback</span>
                <button onClick={() => copyToClipboard('https://yourdomain.com/api/auth/facebook/callback', 'fb-redirect')} className="text-slate-400 hover:text-white">
                  {copiedText === 'fb-redirect' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )
        },
        {
          title: 'Request Permissions',
          content: (
            <div className="space-y-3">
              <p>In App Review, request these permissions:</p>
              <ul className="list-disc list-inside ml-4 text-slate-400">
                <li><strong>pages_manage_posts</strong> - Publish content to Pages</li>
                <li><strong>pages_read_engagement</strong> - View engagement metrics</li>
                <li><strong>pages_show_list</strong> - List user's Pages</li>
              </ul>
              <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded mt-3">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Business verification required for production access
                </p>
              </div>
            </div>
          )
        },
        {
          title: 'Get Your Credentials',
          content: (
            <div className="space-y-3">
              <p>From Settings → Basic:</p>
              <ul className="list-disc list-inside ml-4 text-slate-400">
                <li><strong>App ID</strong></li>
                <li><strong>App Secret</strong></li>
              </ul>
              <p className="mt-3">You'll also need a <strong>Page Access Token</strong> for the specific Page you want to post to.</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10',
      steps: [
        {
          title: 'Prerequisites',
          content: (
            <div className="space-y-3">
              <p>Instagram posting requires:</p>
              <ul className="list-disc list-inside ml-4 text-slate-400">
                <li>An <strong>Instagram Business</strong> or <strong>Creator</strong> account</li>
                <li>The Instagram account must be connected to a <strong>Facebook Page</strong></li>
                <li>A Meta Developer App (same as Facebook)</li>
              </ul>
            </div>
          )
        },
        {
          title: 'Connect Instagram to Facebook Page',
          content: (
            <div className="space-y-3">
              <p>1. Go to your Facebook Page Settings</p>
              <p>2. Click "Instagram" in the left menu</p>
              <p>3. Click "Connect Account" and log into Instagram</p>
              <p>4. Authorize the connection</p>
            </div>
          )
        },
        {
          title: 'Add Instagram Graph API',
          content: (
            <div className="space-y-3">
              <p>1. In your Meta Developer App, add <strong>Instagram Graph API</strong></p>
              <p>2. Request these permissions in App Review:</p>
              <ul className="list-disc list-inside ml-4 text-slate-400">
                <li><strong>instagram_basic</strong></li>
                <li><strong>instagram_content_publish</strong></li>
                <li><strong>pages_read_engagement</strong></li>
              </ul>
            </div>
          )
        },
        {
          title: 'Get Instagram Business Account ID',
          content: (
            <div className="space-y-3">
              <p>Use the Graph API Explorer to get your Instagram Business Account ID:</p>
              <div className="bg-slate-900 p-3 rounded font-mono text-sm">
                GET /me/accounts?fields=instagram_business_account
              </div>
              <p className="mt-3 text-slate-400">This ID is required for all Instagram posting operations.</p>
            </div>
          )
        }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header Alert */}
      <Card className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-700/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="bg-amber-500 p-3 rounded-lg">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg">API Credentials Required</h3>
              <p className="text-slate-300 text-sm mt-1">
                To enable automatic posting to social media platforms, you need to set up developer accounts 
                and obtain API credentials. Follow the guides below for each platform.
              </p>
              <p className="text-amber-400 text-sm mt-3">
                Once you have your credentials, contact your developer to integrate them into the system.
              </p>
            </div>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
                Dismiss
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Platform Guides */}
      <div className="space-y-4">
        {platforms.map((platform) => {
          const Icon = platform.icon;
          const isExpanded = expandedPlatform === platform.id;
          
          return (
            <Card key={platform.id} className="bg-slate-800 border-slate-700">
              <button
                onClick={() => setExpandedPlatform(isExpanded ? null : platform.id)}
                className="w-full"
              >
                <CardHeader className="flex flex-row items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className={`${platform.bgColor} p-2 rounded-lg`}>
                      <Icon className={`w-5 h-5 ${platform.color}`} />
                    </div>
                    <CardTitle className="text-white text-lg">{platform.name} Setup Guide</CardTitle>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </CardHeader>
              </button>
              
              {isExpanded && (
                <CardContent className="px-4 pb-4">
                  <div className="space-y-4">
                    {platform.steps.map((step, index) => (
                      <div key={index} className="border-l-2 border-slate-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-slate-700 text-white text-xs px-2 py-1 rounded">
                            Step {index + 1}
                          </span>
                          <h4 className="text-white font-medium">{step.title}</h4>
                        </div>
                        <div className="text-slate-300 text-sm">
                          {step.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Summary Checklist */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            Credentials Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-slate-900 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Linkedin className="w-5 h-5 text-blue-500" />
                <span className="text-white font-medium">LinkedIn</span>
              </div>
              <ul className="text-slate-400 text-sm space-y-1">
                <li>☐ Client ID</li>
                <li>☐ Client Secret</li>
                <li>☐ Company Page verified</li>
              </ul>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Facebook className="w-5 h-5 text-blue-600" />
                <span className="text-white font-medium">Facebook</span>
              </div>
              <ul className="text-slate-400 text-sm space-y-1">
                <li>☐ App ID</li>
                <li>☐ App Secret</li>
                <li>☐ Page Access Token</li>
              </ul>
            </div>
            <div className="bg-slate-900 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Instagram className="w-5 h-5 text-pink-500" />
                <span className="text-white font-medium">Instagram</span>
              </div>
              <ul className="text-slate-400 text-sm space-y-1">
                <li>☐ Business Account connected</li>
                <li>☐ Instagram Business ID</li>
                <li>☐ Same Meta App credentials</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* External Resources */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Helpful Resources</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-3">
            <a 
              href="https://learn.microsoft.com/en-us/linkedin/marketing/getting-started" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              LinkedIn Marketing API Docs
            </a>
            <a 
              href="https://developers.facebook.com/docs/pages-api" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Facebook Pages API Docs
            </a>
            <a 
              href="https://developers.facebook.com/docs/instagram-api" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Instagram Graph API Docs
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
